import React, { useState, useEffect, useRef } from "react";
import { Col, Row, Input, Label, Form, Spinner, Button, Alert } from "reactstrap";
import { editUser, getFarmacias } from "../../../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { createSelector } from "reselect";
import { Link } from "react-router-dom";
import { uploadFotoPerfilById, deleteFotoPerfilById } from "../../../helpers/backend_helper";
import avatarDefault from "../../../assets/images/users/avatar-1.jpg";

const EditarUsuario = ({ usuario }) => {
  const dispatch = useDispatch();
  const selectLayoutState = (state) => state;

  const farmaciasPageData = createSelector(selectLayoutState, (state) => ({
    farmacias: state.Farmacias.farmacias,
  }));

  const { farmacias } = useSelector(farmaciasPageData);

  useEffect(() => {
    if (!farmacias || farmacias.length === 0) {
      dispatch(getFarmacias());
    } else {
      setFarmaciasLoaded(true);
    }
  }, [dispatch, farmacias]);

  const [nombre, setNombre] = useState(usuario.nombre);
  const [apellido, setApellido] = useState(usuario.apellido);
  const [correo, setCorreo] = useState(usuario.correo);
  const [telefono, setTelefono] = useState(usuario.telefono);
  const [usuarioRol, setUsuarioRol] = useState(usuario.usuario);
  const [creado, setCreado] = useState(usuario.creado);
  const [activo, setActivo] = useState(usuario.activo);
  const [contrasena, setContrasena] = useState("");
  const [perfil, setPerfil] = useState(usuario.perfil);
  const [farmacia, setFarmacia] = useState(usuario.farmacia);
  const opcionesPerfil = ["Administrador", "MyHealth", "Farmacia", "Delivery"];
  const [perfilOptionSelect, setPerfilOptionSelect] = useState({
    value: usuario.perfil,
    label: usuario.perfil,
  });

  const [activoOptionSelect, setActivoOptionSelect] = useState({
    value: usuario.activo,
    label: usuario.activo ? "Si" : "No",
  });

  const [farmaciaOptionSelect, setFarmaciaOptionSelect] = useState({
    value: "",
    label: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const [farmaciasLoaded, setFarmaciasLoaded] = useState(false);

  // Estados para foto de perfil
  const [fotoPerfil, setFotoPerfil] = useState(usuario.foto_perfil || null);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotoError, setFotoError] = useState(null);
  const fileInputRef = useRef(null);

  // Custom password validation function - solo valida si hay contraseña (opcional en edición)
  const validatePassword = (password) => {
    // Si está vacío, no hay error (contraseña opcional en edición)
    if (!password || password.length === 0) {
      return null;
    }
    
    const errors = [];

    if (password.length < 8) {
      errors.push("La contraseña debe tener al menos 8 caracteres.");
    }

    if (!/[a-z]/.test(password)) {
      errors.push("La contraseña debe contener al menos una letra minúscula.");
    }

    if (!/[A-Z]/.test(password)) {
      errors.push("La contraseña debe contener al menos una letra mayúscula.");
    }

    if (!/\d/.test(password)) {
      errors.push("La contraseña debe contener al menos un número.");
    }

    if (!/[!@#$%^&*()_+\-=\]{};':"\\|,.<>?]/.test(password)) {
      errors.push("La contraseña debe contener al menos un símbolo especial.");
    }

    return errors.length > 0 ? errors : null;
  };

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    setContrasena(newPassword);
    const validationErrors = validatePassword(newPassword);
    setPasswordError(validationErrors);
  };

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  }, [dispatch, loading]);

  useEffect(() => {
    if (farmacias && farmacias.length > 0) {
      const farmacia = farmacias[0]?.find(
        (farmacia) => farmacia.id === usuario.farmacia
      );
      if (farmacia) {
        setFarmaciaOptionSelect({
          value: usuario.farmacia,
          label: farmacia.nombre,
        });
      }
    }
  }, [farmacias, usuario.farmacia]);

  useEffect(() => {
    if (perfil !== "Farmacia" && perfil !== "Delivery") {
      setFarmacia(null);
      setFarmaciaOptionSelect({ value: "", label: "" });
    }
  }, [perfil]);

  // Función para manejar la subida de foto de perfil
  const handleFotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFotoError('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFotoError('La imagen no debe superar los 5MB');
      return;
    }

    setUploadingFoto(true);
    setFotoError(null);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result;
          const response = await uploadFotoPerfilById(usuario.id, base64);
          
          if (response?.data?.data?.foto_perfil) {
            setFotoPerfil(response.data.data.foto_perfil);
          }
        } catch (err) {
          setFotoError('Error al subir la imagen');
          console.error(err);
        } finally {
          setUploadingFoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setFotoError('Error al procesar la imagen');
      setUploadingFoto(false);
    }
  };

  const handleDeleteFoto = async () => {
    if (!fotoPerfil) return;
    
    setUploadingFoto(true);
    try {
      await deleteFotoPerfilById(usuario.id);
      setFotoPerfil(null);
    } catch (err) {
      setFotoError('Error al eliminar la imagen');
    } finally {
      setUploadingFoto(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const updatedUser = {
      id: usuario.id,
      nombre: nombre.trim(),
      apellido: apellido.trim(),
      correo: correo,
      telefono: telefono.trim(),
      usuario: usuarioRol.trim(),
      activo: activo,
      contrasena: contrasena.trim(),
      perfil: perfil,
      farmacia_id: farmacia,
      creado: creado,
      actualizado: null,
    };
    dispatch(editUser(updatedUser));
    setLoading(true);
  };

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit}>
        {/* Sección de Foto de Perfil */}
        <Row className="mb-4 p-3">
          <Col lg={12}>
            <div className="d-flex align-items-center gap-4">
              <div className="position-relative">
                <img
                  src={fotoPerfil || avatarDefault}
                  alt="Foto de perfil"
                  className="rounded-circle"
                  style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                  onError={(e) => { e.target.src = avatarDefault; }}
                />
                {uploadingFoto && (
                  <div 
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-circle"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    <Spinner color="light" size="sm" />
                  </div>
                )}
              </div>
              <div>
                <h6 className="mb-2">Foto de Perfil</h6>
                <p className="text-muted mb-2 small">JPG, PNG o GIF. Máximo 5MB.</p>
                <div className="d-flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFotoChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFoto}
                  >
                    <i className="ri-upload-2-line me-1"></i>
                    Subir
                  </Button>
                  {fotoPerfil && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={handleDeleteFoto}
                      disabled={uploadingFoto}
                    >
                      <i className="ri-delete-bin-line me-1"></i>
                      Eliminar
                    </Button>
                  )}
                </div>
                {fotoError && (
                  <Alert color="danger" className="mt-2 mb-0 py-1 px-2">
                    {fotoError}
                  </Alert>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <hr className="mb-3" />
        <Row lg={12} className="p-3">
          <Col xxl={4} md={4}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="nombre">
                Nombre <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                className="form-control"
                id="nombre"
                name="nombre"
                minLength={3}
                maxLength={50}
                value={nombre}
                required
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>
          </Col>
          <Col xxl={4} md={4}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="apellido">
                Apellido <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                className="form-control"
                id="apellido"
                name="apellido"
                minLength={3}
                maxLength={50}
                required
                value={apellido}
                onChange={(e) => setApellido(e.target.value)}
              />
            </div>
          </Col>
          <Col xxl={4} md={4}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="correo">
                Email <span className="text-danger">*</span>
              </Label>

              <Input
                type="email"
                className="form-control"
                id="correo"
                name="correo"
                required
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
              />
            </div>
          </Col>
          <Col xxl={4} md={4}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="telefono">
                Teléfono <span className="text-danger">*</span>
              </Label>
              <Input
                type="tel"
                className="form-control"
                id="telefono"
                name="telefono"
                required
                minLength={13}
                maxLength={13}
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
              />
            </div>
          </Col>
          <Col xxl={4} md={4}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="usuario">
                Usuario <span className="text-danger">*</span>
              </Label>

              <Input
                type="text"
                className="form-control"
                id="usuario"
                name="usuario"
                required
                minLength={5}
                maxLength={50}
                value={usuarioRol}
                onChange={(e) => setUsuarioRol(e.target.value)}
              />
            </div>
          </Col>

          <Col xxl={4} md={4}>
            <div className="mb-3 position-relative">
              <Label className="form-label" htmlFor="contrasena">
                Contraseña <small className="text-muted">(dejar vacío para mantener actual)</small>
              </Label>

              <div className="position-relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  className={`form-control pr-5 ${
                    passwordError ? "is-invalid" : ""
                  }`}
                  id="contrasena"
                  name="contrasena"
                  placeholder="Nueva contraseña (opcional)"
                  minLength={8}
                  maxLength={12}
                  value={contrasena}
                  onChange={handlePasswordChange}
                />

                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ height: "100%", zIndex: 10 }}
                >
                  <i className={`ri-eye${showPassword ? "-off" : ""}-line`}></i>
                </button>

                {passwordError && (
                  <div className="invalid-feedback">
                    <ul>
                      {passwordError.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </Col>

          <Col xxl={4} md={4}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="perfil">
                Perfil <span className="text-danger">*</span>
              </Label>

              <Select
                defaultValue={perfilOptionSelect}
                onChange={(selectedOption) => {
                  setPerfil(selectedOption.value);
                }}
                options={opcionesPerfil.map((item) => ({
                  value: item,
                  label: item,
                }))}
                id="perfil"
                className="js-example-basic-single mb-0"
                name="perfil"
                required
              />
            </div>
          </Col>

          <Col xxl={4} md={4}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="activo">
                Activo <span className="text-danger">*</span>
              </Label>

              <Select
                defaultValue={activoOptionSelect}
                onChange={(selectedOption) => {
                  setActivo(selectedOption.value);
                }}
                options={[
                  { value: true, label: "Si" },
                  { value: false, label: "No" },
                ]}
                required
                id="activo"
                className="js-example-basic-single mb-0"
                name="activo"
              />
            </div>
          </Col>

          <Col xxl={4} md={4}>
            {(perfil === "Farmacia" || perfil === "Delivery") && (
              <div className="mb-3">
                <Label className="form-label" htmlFor="activo">
                  Farmacia <span className="text-danger">*</span>
                </Label>

                {!farmaciasLoaded ? (
                  <Spinner size="sm" color="primary" />
                ) : farmacias.length === 0 ? (
                  <p>No hay farmacias disponibles</p>
                ) : (
                  <Select
                    defaultValue={farmaciaOptionSelect}
                    onChange={(selectedOption) => {
                      setFarmacia(selectedOption.value);
                    }}
                    options={farmacias[0]?.map((farmacia) => ({
                      value: farmacia.id,
                      label: farmacia.nombre,
                    }))}
                    required
                    id="farmacia"
                    className="js-example-basic-single mb-0"
                    name="farmacia"
                  />
                )}
              </div>
            )}
          </Col>
        </Row>

        <Row className="align-items-end">
          <Col lg={6}>
            <div className="mt-4 mb-3">
              <Link to="/usuarios">
                <Button className="btn btn-info btn-label left ms-auto nexttab nexttab">
                  <i className="ri-arrow-left-line label-icon align-middle fs-16 ms-2"></i>
                  Regresar a Usuarios
                </Button>
              </Link>
            </div>
          </Col>
          <Col lg={6}>
            <div className="mt-4 mb-3 text-end">
              <button
                type="submit"
                className="btn btn-success btn-label right ms-auto nexttab nexttab"
              >
                {loading ? (
                  <Spinner size="sm" className="me-2">
                    {" "}
                    Loading...{" "}
                  </Spinner>
                ) : null}
                <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                Editar Usuario
              </button>
            </div>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  );
};

export default EditarUsuario;
